import { useState, useEffect, useRef, useCallback } from 'react';
import { subscribeToRequests, clearCapturedRequests } from '~/utils/debug.client';

/**
 * Debug Widget
 * 
 * This component provides a sleek, modern debug tool for site administrators to monitor and inspect
 * all API requests made during the current page load. Features include:
 * 
 * - Tracks all fetch and XMLHttpRequest calls
 * - Displays request details including URL, method, status, and duration
 * - Shows response data in a readable format with syntax highlighting
 * - Captures and displays error details when requests fail
 * - Can be toggled on/off with an elegant icon in the lower right corner
 * - Fully draggable and resizable interface
 * - Smooth animations and transitions for a polished feel
 * - Search and filter functionality for requests
 * - Keyboard shortcuts for common actions
 * - Responsive design that works well on all screen sizes
 * 
 * The widget is only visible to users with the 'site_admin' role or account type,
 * as controlled by the condition in root.tsx.
 * 
 * Note: Request interception is set up in debug.client.ts, which is imported early
 * in the application lifecycle to ensure all requests are captured, including those
 * made during the initial page load.
 */

// Define types for request tracking
interface RequestData {
  id: string;
  url: string;
  method: string;
  status?: number;
  statusText?: string;
  responseData?: any;
  error?: Error;
  startTime: number;
  endTime?: number;
  duration?: number;
}

/**
 * Debug Widget Component
 * 
 * A debug tool that shows all requests made for the current page load,
 * including response data and error details.
 * Only visible to site admins.
 */
export default function DebugWidget() {
  // State for tracking requests
  const [requests, setRequests] = useState<RequestData[]>([]);
  // State for toggling the widget visibility
  const [isOpen, setIsOpen] = useState(false);
  // State for tracking if the widget is being dragged
  const [isDragging, setIsDragging] = useState(false);
  // State for the selected request to view details
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);
  // Ref for the widget container
  const widgetRef = useRef<HTMLDivElement>(null);
  // State for widget position
  const [position, setPosition] = useState({ x: 20, y: 20 });
  // State for drag start position
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  // State for widget size
  const [size, setSize] = useState({ width: 800, height: 600 });
  // State for resizing
  const [isResizing, setIsResizing] = useState(false);
  // State for resize start
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  // State for search term
  const [searchTerm, setSearchTerm] = useState('');
  // State for theme (light/dark)
  const [darkMode, setDarkMode] = useState(false);

  // Effect to subscribe to request updates from debug.client.ts
  useEffect(() => {
    // Subscribe to request updates
    const unsubscribe = subscribeToRequests((updatedRequests) => {
      setRequests(updatedRequests);
    });
    
    // Cleanup function to unsubscribe
    return () => {
      unsubscribe();
    };
  }, []);

  // Handle mouse down for dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === widgetRef.current) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  // Handle mouse move for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);
  
  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizing) {
        // Calculate new width and height
        const newWidth = Math.max(400, resizeStart.width + (e.clientX - resizeStart.x));
        const newHeight = Math.max(300, resizeStart.height + (e.clientY - resizeStart.y));
        
        setSize({
          width: newWidth,
          height: newHeight
        });
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      // Change cursor during resize
      document.body.style.cursor = 'nwse-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Reset cursor
      if (isResizing) {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, [isResizing, resizeStart]);
  
  // Toggle widget visibility
  const toggleWidget = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Toggle dark mode
  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => !prev);
  }, []);

  // Clear all requests
  const clearRequests = useCallback(() => {
    clearCapturedRequests(); // This will trigger the subscribeToRequests callback with an empty array
    setSelectedRequest(null);
  }, []);

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when widget is open
      if (!isOpen) return;
      
      // Escape key to close widget
      if (e.key === 'Escape') {
        toggleWidget();
      }
      
      // Ctrl+K to focus search
      if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const searchInput = document.getElementById('debug-search-input');
        if (searchInput) {
          (searchInput as HTMLInputElement).focus();
        }
      }
      
      // Ctrl+D to toggle dark mode
      if (e.key === 'd' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        toggleDarkMode();
      }
      
      // Ctrl+L to clear requests
      if (e.key === 'l' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        clearRequests();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, toggleWidget, toggleDarkMode, clearRequests]);

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
  }, [size]);

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Filter requests based on search term
  const filteredRequests = useCallback(() => {
    if (!searchTerm) return requests;
    return requests.filter(req => 
      req.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.method.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (req.status?.toString() || '').includes(searchTerm)
    );
  }, [requests, searchTerm]);

  // Format duration in ms
  const formatDuration = (duration?: number) => {
    if (!duration) return 'Pending...';
    return `${duration.toFixed(2)} ms`;
  };

  // Get status color based on HTTP status code
  const getStatusColor = (status?: number) => {
    if (!status) return 'bg-gray-500'; // Pending
    if (status >= 200 && status < 300) return 'bg-green-500'; // Success
    if (status >= 300 && status < 400) return 'bg-blue-500'; // Redirect
    if (status >= 400 && status < 500) return 'bg-yellow-500'; // Client Error
    return 'bg-red-500'; // Server Error
  };

  // Get the selected request details
  const selectedRequestData = selectedRequest 
    ? requests.find(req => req.id === selectedRequest) 
    : null;

  // Get theme-based classes
  const getThemeClasses = () => {
    return darkMode 
      ? {
          bg: 'bg-gray-900',
          text: 'text-gray-100',
          border: 'border-gray-700',
          header: 'bg-gray-800',
          sidebar: 'bg-gray-800',
          hover: 'hover:bg-gray-700',
          input: 'bg-gray-800 border-gray-700 text-gray-100',
          button: 'bg-blue-600 hover:bg-blue-700',
          code: 'bg-gray-800 text-gray-100'
        }
      : {
          bg: 'bg-white',
          text: 'text-gray-800',
          border: 'border-gray-200',
          header: 'bg-blue-600',
          sidebar: 'bg-gray-50',
          hover: 'hover:bg-gray-100',
          input: 'bg-white border-gray-300 text-gray-800',
          button: 'bg-blue-600 hover:bg-blue-700',
          code: 'bg-gray-50 text-gray-800'
        };
  };

  const theme = getThemeClasses();
  
  // Filter requests based on search term
  const filteredRequestsList = searchTerm 
    ? requests.filter(req => 
        req.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.method.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (req.status?.toString() || '').includes(searchTerm)
      )
    : requests;

  // CSS for animations
  const animationStyles = `
    @keyframes fadeIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    
    @keyframes slideIn {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    
    @keyframes pulse {
      0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); }
      70% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
      100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
    }
    
    .debug-widget-item {
      animation: slideIn 0.3s ease-out forwards;
      animation-delay: calc(var(--index) * 0.05s);
      opacity: 0;
    }
    
    .debug-widget-pulse {
      animation: pulse 2s infinite;
    }
  `;

  return (
    <>
      {/* CSS Animations */}
      <style>{animationStyles}</style>
    
      {/* Toggle Button */}
      <button
        onClick={toggleWidget}
        className={`fixed bottom-4 right-4 z-50 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 ${requests.length > 0 ? 'debug-widget-pulse' : ''}`}
        aria-label="Toggle Debug Widget"
        title="Debug Widget (Ctrl+D)"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="h-6 w-6" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" 
          />
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
          />
        </svg>
      </button>

      {/* Debug Widget Panel */}
      {isOpen && (
        <div
          ref={widgetRef}
          className={`fixed z-50 ${theme.bg} ${theme.text} border ${theme.border} rounded-lg shadow-2xl overflow-hidden transition-all duration-300 ease-in-out`}
          style={{
            bottom: position.y,
            right: position.x,
            width: `${size.width}px`,
            height: `${size.height}px`,
            maxWidth: '95vw',
            maxHeight: '90vh',
            cursor: isDragging ? 'grabbing' : 'grab',
            opacity: 1,
            transform: 'scale(1)',
            animation: 'fadeIn 0.3s ease-out'
          }}
          onMouseDown={handleMouseDown}
        >
          {/* Widget Header */}
          <div className={`${theme.header} text-white p-3 flex justify-between items-center`}>
            <div className="flex items-center">
              <h2 className="text-lg font-semibold mr-2">Debug Widget</h2>
              <span className="text-xs bg-white bg-opacity-20 px-2 py-0.5 rounded">
                {requests.length} {requests.length === 1 ? 'request' : 'requests'}
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={toggleDarkMode}
                className="text-white hover:text-gray-300 transition-colors p-1 rounded hover:bg-black hover:bg-opacity-20"
                title={darkMode ? "Switch to Light Mode (Ctrl+D)" : "Switch to Dark Mode (Ctrl+D)"}
              >
                {darkMode ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>
              <button
                onClick={clearRequests}
                className="text-white hover:text-gray-300 transition-colors p-1 rounded hover:bg-black hover:bg-opacity-20"
                title="Clear Requests (Ctrl+L)"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-5 w-5" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                  />
                </svg>
              </button>
              <button
                onClick={toggleWidget}
                className="text-white hover:text-gray-300 transition-colors p-1 rounded hover:bg-black hover:bg-opacity-20"
                title="Close (Esc)"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-5 w-5" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className={`p-2 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} border-b ${theme.border}`}>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                id="debug-search-input"
                type="text"
                placeholder="Search requests... (Ctrl+K)"
                className={`block w-full pl-10 pr-3 py-2 border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  <svg className="h-4 w-4 text-gray-500 hover:text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Widget Content */}
          <div className="flex flex-col md:flex-row h-full" style={{ height: 'calc(100% - 96px)' }}>
            {/* Requests List */}
            <div className={`w-full md:w-1/3 border-b md:border-b-0 md:border-r ${theme.border} overflow-auto ${theme.sidebar} ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`} style={{ maxHeight: '40vh', height: 'auto', minHeight: '150px' }}>
              <div className="overflow-auto h-full">
                {filteredRequestsList.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {searchTerm ? 'No matching requests found' : 'No requests captured yet'}
                  </div>
                ) : (
                  <ul className={`divide-y ${theme.border}`}>
                    {filteredRequestsList.map((request, index) => (
                      <li 
                        key={request.id}
                        className={`p-3 ${theme.hover} cursor-pointer transition-colors duration-150 ${selectedRequest === request.id ? (darkMode ? 'bg-gray-700' : 'bg-blue-50') : ''} debug-widget-item`}
                        style={{ '--index': index } as React.CSSProperties}
                        onClick={() => setSelectedRequest(request.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className={`w-3 h-3 rounded-full mr-2 ${getStatusColor(request.status)}`}></span>
                            <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>{request.method}</span>
                          </div>
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatDuration(request.duration)}</span>
                        </div>
                        <div className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} truncate`}>
                          {request.url}
                        </div>
                        {request.status && (
                          <div className="mt-1 text-xs">
                            <span className={`px-1.5 py-0.5 rounded-full ${getStatusColor(request.status)} text-white`}>
                              {request.status}
                            </span>
                            <span className={`ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{request.statusText}</span>
                          </div>
                        )}
                        {request.error && (
                          <div className="mt-1 text-xs text-red-500">
                            Error: {request.error.message}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Request Details */}
            <div className="w-full md:w-2/3 overflow-auto flex-grow">
              {selectedRequestData ? (
                <div className="p-4">
                  <h3 className={`text-lg font-medium ${theme.text} mb-4 flex items-center`}>
                    <span className={`w-3 h-3 rounded-full mr-2 ${getStatusColor(selectedRequestData.status)}`}></span>
                    {selectedRequestData.method} Request Details
                  </h3>
                  
                  <div className="mb-4">
                    <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>General</h4>
                    <div className={`${theme.code} p-3 rounded-md border ${theme.border}`}>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>URL:</span>
                          <div className="font-mono text-xs break-all mt-1">{selectedRequestData.url}</div>
                        </div>
                        <div>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Method:</span>
                          <div className="font-medium mt-1">{selectedRequestData.method}</div>
                        </div>
                        <div>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Status:</span>
                          <div className="mt-1">
                            {selectedRequestData.status ? (
                              <span className={`px-2 py-0.5 rounded-full ${getStatusColor(selectedRequestData.status)} text-white text-xs`}>
                                {selectedRequestData.status} {selectedRequestData.statusText}
                              </span>
                            ) : (
                              <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Pending...</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Duration:</span>
                          <div className="font-medium mt-1">{formatDuration(selectedRequestData.duration)}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedRequestData.error ? (
                    <div className="mb-4">
                      <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Error</h4>
                      <div className={`${darkMode ? 'bg-red-900 border-red-800' : 'bg-red-50 border-red-200'} border p-3 rounded-md`}>
                        <div className="text-red-500 text-sm">{selectedRequestData.error.message}</div>
                        {selectedRequestData.error.stack && (
                          <pre className={`mt-2 text-xs font-mono ${darkMode ? 'text-red-300 bg-red-950' : 'text-red-800 bg-red-100'} overflow-auto p-2 rounded`}>
                            {selectedRequestData.error.stack}
                          </pre>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Response Data</h4>
                      <div className={`${theme.code} border ${theme.border} p-3 rounded-md`}>
                        {selectedRequestData.responseData ? (
                          <pre className={`text-xs font-mono overflow-auto max-h-96 ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                            {typeof selectedRequestData.responseData === 'string'
                              ? selectedRequestData.responseData
                              : JSON.stringify(selectedRequestData.responseData, null, 2)}
                          </pre>
                        ) : (
                          <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No response data available</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-500 p-4">
                  <svg className="h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>Select a request to view details</p>
                  {searchTerm && filteredRequestsList.length === 0 && (
                    <p className="mt-2 text-sm">No requests match your search criteria</p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Resize Handle */}
          <div 
            className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize"
            onMouseDown={handleResizeStart}
            title="Resize"
          >
            <svg 
              className={`w-6 h-6 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M16 16L22 22M8 16L22 22M16 8L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
        </div>
      )}
    </>
  );
}